/**
 * Os dados da mesa e as ações que mexem neles.
 *
 * O que é LUGAR não mora aqui — vive na URL (`navegacao.jsx`). Aqui fica o que
 * o servidor devolveu, o que está carregando e o que uma ação acabou de fazer.
 * A separação é o que permite recarregar a página e cair exatamente na mesma
 * conversa, com o mesmo filtro.
 *
 * Contextos separados de propósito: `estado` muda o tempo todo, `acoes` é
 * estável. Componente que só age não redesenha quando um dado muda.
 *
 * E três sinais que mudam MUITO moram fora de `estado`, cada um no seu canal:
 * o tamanho da janela (`useJanela`, um quadro por arrasto), o `ocupado` de toda
 * ação (`useOcupado`) e o recado de seis segundos (`useRecado`). Quando os três
 * viviam no objeto único, cada clique, cada balão e cada quadro de redimensionar
 * redesenhava as quase sessenta telas que leem `useEstado()`.
 *
 * As ações moram por área em `estado/` (conversa, caso, tarefas, voz, WhatsApp,
 * mensagem) e o canal de eventos em `estado/canal.js`; aqui fica a costura —
 * carregar, a sessão e o objeto de ações montado uma vez.
 *
 * Regra da casa que aparece aqui: recarrega-se a trilha, nunca o rascunho. O
 * texto sendo digitado mora dentro do componente de redação.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as api from "./api.js";
import { linhaDoCaso } from "./caso.js";
import { matrizDoGrupo } from "./mesa-painel.js";
import { primeiraNaoLida } from "./conversa.js";
import { useNavegacao } from "./navegacao.jsx";
import { usePaginacao } from "./paginacao.js";
import { confirmar } from "@/componentes/pergunta.tsx";
import { INICIAL } from "./estado/inicial.js";
import { useCanalDeEventos } from "./estado/canal.js";
import { useJanela } from "./estado/janela.js";
import { acoesDaConversa } from "./estado/conversa.js";
import { acoesDoCaso } from "./estado/caso.js";
import { acoesDeTarefas } from "./estado/tarefas.js";
import { acoesDoContato } from "./estado/contato.js";
import { acoesDeVoz } from "./estado/voz.js";
import { acoesDoWhatsapp } from "./estado/whatsapp.js";
import { acoesDaMensagem } from "./estado/mensagem.js";
import * as rascunhos from "./estado/rascunhos.js";
import * as push from "./push.js";

/* A mesa já abriu no número de quem atende nesta sessão? (ver `buscarMesa`) */
let numeroPadraoAplicado = false;

export { useJanela };

const EstadoContexto = createContext(null);
const AcoesContexto = createContext(null);

export const useEstado = () => useContext(EstadoContexto);
export const useAcoes = () => useContext(AcoesContexto);

const OcupadoContexto = createContext(null);
const RecadoContexto = createContext(null);

/** Qual ação está rodando agora (`tentar("nome", …)`), ou `null`. */
export const useOcupado = () => useContext(OcupadoContexto);
/** O aviso de rodapé de agora, ou `null`. Quem desenha é `sobreposicoes/Recado.jsx`. */
export const useRecado = () => useContext(RecadoContexto);

/*
 * A releitura reaproveita os OBJETOS que não mudaram.
 *
 * `GET /api/mesa/:id` devolve a conversa inteira a cada evento, e objetos novos
 * fazem `memo` falhar: duzentas bolhas re-renderizam por causa de uma mensagem
 * nova, e as que têm foto piscam enquanto o navegador refaz o layout. Aqui cada
 * mensagem que voltou IGUAL continua sendo a mesma referência de antes — o React
 * repinta só o que de fato mudou.
 *
 * A comparação é rasa e pelos campos que a bolha desenha. Barato o bastante para
 * rodar a cada evento, específico o bastante para não segurar mudança de verdade.
 */
const MESMA_BOLHA = ["texto", "entrega", "reacao", "apagado_em", "editado_em", "lida_em", "arquivo", "transcricao", "tipo"];

export function comIdentidadePreservada(anterior, novo, trocouDeCaso) {
  if (trocouDeCaso || !anterior?.mensagens?.length || !novo?.mensagens?.length) return novo;

  const antigas = new Map(anterior.mensagens.map((m) => [m.id, m]));
  let mudou = false;
  const mensagens = novo.mensagens.map((m) => {
    const velha = antigas.get(m.id);
    if (!velha) {
      mudou = true;
      return m;
    }
    const igual =
      MESMA_BOLHA.every((c) => velha[c] === m[c]) && JSON.stringify(velha.dados ?? null) === JSON.stringify(m.dados ?? null);
    if (!igual) mudou = true;
    return igual ? velha : m;
  });
  /* Nem a LISTA muda de identidade quando nada mudou: sem isto o `useMemo` da
   trilha recalcula blocos e faixas a cada evento, por nada. */
  if (!mudou && mensagens.length === anterior.mensagens.length) return { ...novo, mensagens: anterior.mensagens };
  return { ...novo, mensagens };
}

/*
 * A janela da conversa ANDA, e o que sai por cima não pode sumir.
 *
 * A releitura traz as N últimas: chegando três mensagens com o caso aberto, as
 * três mais velhas da janela saem dela — e elas não estão em `antigas`, que só
 * guarda o que veio de ANTES da primeira janela. Sem costura elas sumiam do meio
 * da conversa até alguém recarregar. Com a janela de 50 isso deixou de ser raro.
 */
export function costurarAntigas(antigas, anterior, novo) {
  const ids = (novo?.mensagens ?? []).map((m) => m.id).filter((id) => typeof id === "number");
  if (!ids.length || !anterior?.mensagens?.length) return antigas;
  const menor = Math.min(...ids);
  const ja = new Set((antigas ?? []).map((m) => m.id));
  const sairam = anterior.mensagens.filter((m) => typeof m.id === "number" && m.id < menor && !ja.has(m.id));
  return sairam.length ? [...(antigas ?? []), ...sairam] : antigas;
}

export function Provedor({ children }) {
  const [estado, setEstado] = useState(INICIAL);
  const [ocupado, setOcupado] = useState(null);
  /* O `ocupado` de agora para as ações lerem sem esperar o render (a rolagem da
     trilha pede o histórico de trás e não pode pedir duas vezes). */
  const ocupadoRef = useRef(null);
  const [recado, setRecado] = useState(null);
  const nav = useNavegacao();
  /* Espelho para as ações lerem o valor de agora sem depender de closure. */
  const ref = useRef(estado);
  ref.current = estado;
  /* As próprias ações num ref: o gravador de áudio precisa chamar `pararRecado`
     de dentro de um `onerror`/`setInterval`, que nasceram antes do objeto. */
  const acoesRef = useRef(null);
  const navRef = useRef(nav);
  navRef.current = nav;

  const mudar = useCallback((mudanca) => {
    setEstado((atual) => {
      const remendo = typeof mudanca === "function" ? mudanca(atual) : mudanca;
      /* `null` (ou nada) é "não mudou": sem esta linha o espalhamento criava um
         objeto NOVO e idêntico, e as quarenta e três telas que leem
         `useEstado()` refaziam a árvore para pintar o mesmo pixel. */
      if (!remendo) return atual;
      return { ...atual, ...remendo };
    });
  }, []);

  /* A montagem em curso, para o Cancelar cortar o pedido e o servidor não gravar. */
  const abortoMinuta = useRef(null);
  const relogioRecado = useRef(null);
  /* O "digitando…" apaga sozinho: ver o tratamento do evento de presença. */
  const relogioPresenca = useRef(null);
  /* A rajada de eventos esperando virar UMA releitura — ver o canal de eventos. */
  const releitura = useRef({ mesa: false, caso: 0, tipos: new Set(), relogio: null });
  /* Qual leitura do copiloto é a de agora. A do assistente demora dezenas de
     segundos: sem esta senha, a resposta de um pedido velho (outro caso, ou o
     mesmo caso antes da última mensagem) chegava depois e pintava por cima da
     nova — o cartão preto afirmando "só duas saudações" sobre uma conversa que
     já tinha o relato inteiro. */
  const leituraIA = useRef(0);
  const avisar = useCallback((texto) => {
    setRecado(texto);
    clearTimeout(relogioRecado.current);
    relogioRecado.current = setTimeout(() => setRecado(null), 6000);
  }, []);

  /** Roda a ação marcando quem está ocupado e traduzindo a falha em recado. */
  const tentar = useCallback(
    async (nome, fn) => {
      ocupadoRef.current = nome;
      setOcupado(nome);
      try {
        return await fn();
      } catch (e) {
        avisar(e.message);
        return null;
      } finally {
        ocupadoRef.current = null;
        setOcupado(null);
      }
    },
    [avisar],
  );

  /* ------------------------------------------------------------ carregar */

  /**
   * A leitura do copiloto para um caso.
   *
   * `limpar` é só na abertura: na releitura o cartão antigo fica no lugar até o
   * novo chegar, porque piscar "consultando o copiloto…" a cada mensagem seria
   * pior do que ler a frase de um segundo atrás.
   *
   * O servidor guarda a leitura pela impressão do caso, então pedir de novo sem
   * nada ter mudado é uma consulta ao banco — e, quando mudou, é exatamente a
   * releitura que faltava.
   */
  const lerCopiloto = useCallback(
    async (id, { limpar = false, renovar = false, modelo = true } = {}) => {
      const vez = ++leituraIA.current;
      if (limpar) mudar({ ia: null });
      const valida = () => vez === leituraIA.current && navRef.current.casoId === id;
      try {
        const ia = await api.copiloto(id, { renovar, modelo });
        if (valida()) mudar({ ia });
      } catch (e) {
        if (valida()) mudar({ ia: { erro: e.message } });
      }
    },
    [mudar],
  );

  const carregarCaso = useCallback(
    async (id, { comCopiloto = true } = {}) => {
      if (!id) return;
      mudar({ casoErro: null });
      /* Outro caso: o anterior sai da tela JÁ. Ficando até a resposta, o
         cabeçalho dizia B (vem da lista) com a conversa, a pauta e a ficha
         EDITÁVEL de A embaixo — dava para gravar o CPF no cliente errado. Sem
         `caso`, toda a coluna mostra o esqueleto de "abrindo". Reabrir o mesmo
         caso não limpa: é releitura, e piscar seria pior. */
      if (ref.current.caso && ref.current.caso.registro?.id !== id) mudar({ caso: null, provas: [], presenca: null, ia: null });
      let caso;
      try {
        caso = await api.caso(id);
      } catch (e) {
        /* Falha de um caso que a pessoa já deixou não fala sobre a tela de agora. */
        if (navRef.current.casoId !== id) return;
        /*
         * Releitura de um caso que JÁ está na tela não pode apagá-lo: rede
         * falhando no meio de um evento vira recado, e a conversa aberta
         * continua onde está. Quando não há o que mostrar, a falha OCUPA a tela
         * — é ela que diz se o caso não existe, se é de outro escritório ou se
         * o servidor não respondeu.
         */
        if (ref.current.caso?.registro?.id === id) avisar(e.message);
        /* O `id` viaja junto: limpar a falha é efeito, e efeito roda DEPOIS da
           pintura — sem ele, clicar num caso bom logo depois de um link quebrado
           pintava "não encontrado" por um quadro antes de abrir a conversa. */ else
          mudar({ caso: null, casoErro: { id, status: e.status ?? 0, mensagem: e.message, daCasa: Boolean(e.daCasa) } });
        return;
      }
      /* Resposta que chegou tarde demais não entra: quem clica em A, desiste e
         abre B recebia a conversa de A por cima da de B — e a tela ficava
         "Abrindo o atendimento…" para sempre, porque o caso guardado nunca mais
         batia com o id da URL. */
      if (navRef.current.casoId !== id) return;
      /* Abrir a conversa marca lida NO APARELHO do cliente: são os dois tiques
         azuis que ele vê. Vale por si — provedor sem isso responde que não há. */
      const temNaoLida = (caso.mensagens ?? []).some((m) => m.direcao === "entrada" && !m.lida_em);
      if (temNaoLida) api.marcarLidoNoAparelho(id).catch(() => {});
      /*
       * O divisor de não lidas nasce AQUI e não se recalcula.
       *
       * Marcar lida publica evento, o evento relê o caso, e a releitura já vem
       * com `lida_em` preenchido: um divisor calculado na pintura viveria um
       * quadro. Fixado por caso — trocar de conversa recomeça a conta.
       */
      const trocouDeCaso = ref.current.caso?.registro?.id !== id;
      /*
       * Marcar lida no CRM vem DEPOIS de ler a conversa, e não antes.
       *
       * Estava no efeito da rota, disparado junto com `carregarCaso`: servidor
       * rápido marcava tudo lido primeiro e a resposta chegava sem uma única não
       * lida — o divisor "onde eu parei" simplesmente não nascia. Aqui o retrato
       * já está na mão, e é dele que o divisor sai.
       *
       * Em TODA leitura com não lida, não só na troca de caso: `caso` não se
       * apaga ao voltar para a lista, então reabrir a última conversa (que
       * recebeu mensagem nesse meio) não era "troca" e o selo só saía
       * recarregando a página. Não vira laço: o servidor só publica evento
       * quando alguma linha mudou.
       */
      if (temNaoLida) api.marcarLida(id).catch(() => {});
      mudar({
        /* Reaproveita as bolhas que voltaram iguais: é o que impede duzentas
           mensagens de re-renderizarem por causa de uma. */
        caso: comIdentidadePreservada(ref.current.caso, caso, trocouDeCaso),
        provas: caso.provas,
        presenca: caso.presenca ?? null,
        ...(trocouDeCaso
          ? {
              antigas: [],
              /* Conversa que veio menor que o teto da rota JÁ é inteira: sem
                 isto, abrir qualquer atendimento de dez mensagens custava um
                 pedido a mais para o servidor responder "não tem mais nada". */
              antigasFim: (caso.mensagens?.length ?? 0) < api.TAMANHO_DA_CONVERSA,
              selecionadas: null,
              naoLidasDesde: primeiraNaoLida(caso.mensagens),
            }
          : { antigas: costurarAntigas(ref.current.antigas, ref.current.caso, caso) }),
      });
      if (!comCopiloto) return;
      await lerCopiloto(id, { limpar: true });
    },
    [avisar, lerCopiloto, mudar],
  );

  /*
   * Uma página da mesa — e, de carona, o resto do que a rota agregada manda.
   *
   * A identidade desta função é o que diz "isto é outra lista": mudou a busca ou
   * a pastilha, `usePaginacao` recomeça a rolagem do zero. Por isso o recorte
   * está nas dependências e não dentro de um ref.
   *
   * Sem sessão não há pedido: o hook monta antes do login, e um 401 por página
   * só encheria o console de vermelho na tela de entrar.
   */
  const idDoUsuario = estado.usuario?.id ?? 0;
  const buscarMesa = useCallback(
    async (offset, limite, sinal) => {
      if (!idDoUsuario) return { itens: [], total: 0 };
      const { corpo, total } = await api.mesaPagina({
        offset,
        limite,
        q: nav.termo,
        filtro: nav.filtro,
        etapa: nav.etapaDaMesa,
        resp: nav.respDaMesa,
        tag: nav.tagDaMesa,
        grupo: nav.grupoDaMesa,
        criativo: nav.criativoDaMesa,
        numero: nav.numeroDaMesa,
        sinal,
      });
      mudar({
        contadores: corpo.contadores ?? {},
        /* A grade etapa × responsável, contada pelo servidor sobre o recorte.
           O servidor não filtra pelo GRUPO (no painel ele é eixo, ver
           `mesa-painel.js`); o filtro da coluna recebe a matriz do grupo, como
           sempre recebeu, e o painel e os cartões de grupo a inteira. */
        matrizInteira: corpo.matriz ?? [],
        matriz: matrizDoGrupo(corpo.matriz ?? [], corpo.etapas, nav.grupoDaMesa),
        etapas: corpo.etapas,
        /* Os três meta-grupos com o rótulo que o domínio escreve: o corte das
           etapas é do servidor, e a tela não tem cópia dele. */
        ...(corpo.grupos ? { gruposDaMesa: corpo.grupos } : {}),
        ...(corpo.campos ? { campos: corpo.campos } : {}),
        ...(corpo.listas ? { listas: corpo.listas } : {}),
        equipe: corpo.equipe,
        ...(corpo.fotos ? { fotos: corpo.fotos } : {}),
        avisos: corpo.avisos,
        envio: corpo.envio,
        /* As respostas prontas do escritório, para o compositor não cair na queda
           de cinco textos fixos em toda sessão que não passou por `/rapidas` —
           ver `rotas/mesa.ts`. A tela de Rápidas sobrescreve com a versão que tem
           `previa`, e `Rapidas.jsx` já cai no texto cru quando ela não vem. */
        ...(corpo.rapidas ? { rapidas: corpo.rapidas } : {}),
        /* Prazo vencido, para a pastilha do trilho acender sem abrir a tela. */
        tarefasAtrasadas: corpo.tarefas_atrasadas ?? 0,
        /* As capacidades do provedor chegam com a mesa (sem credencial): é o que
           apaga o botão de reagir ou editar antes de alguém clicar. */
        wa: corpo.whatsapp ?? null,
        /* O catálogo de etiquetas com a contagem, do servidor: contar na tela
           contaria só a página carregada. */
        catalogoDeTags: corpo.tags ?? [],
        /* Os números de WhatsApp do setor, com o estado de cada um: com mais de
           um, a linha e o cabeçalho dizem por qual o caso fala. */
        numerosDoSetor: corpo.numeros ?? [],
      });
      /* O número de quem atende (02/11): a mesa abre filtrada nele UMA vez por
         sessão. Depois a pessoa troca à vontade — tirar o filtro não o devolve. */
      if (!numeroPadraoAplicado && !nav.numeroDaMesa) {
        numeroPadraoAplicado = true;
        if (corpo.meu_numero) navRef.current.ajustar({ numero: String(corpo.meu_numero) });
      }
      return { itens: corpo.atendimentos ?? [], total };
    },
    [
      idDoUsuario,
      mudar,
      nav.filtro,
      nav.termo,
      nav.etapaDaMesa,
      nav.respDaMesa,
      nav.tagDaMesa,
      nav.grupoDaMesa,
      nav.criativoDaMesa,
      nav.numeroDaMesa,
    ],
  );
  const listaDaMesa = usePaginacao({ buscar: buscarMesa, tamanho: api.TAMANHO_DA_MESA });

  /*
   * Recarregar a mesa é reler o TOPO, não recomeçar.
   *
   * As quinze ações que chamam isto acontecem com alguém olhando a lista — e
   * quem rolou até o caso 300 não pode voltar ao começo porque marcou uma
   * mensagem como lida. É a mesma regra da conversa: recarregue a trilha, nunca
   * o rascunho.
   */
  const carregarMesa = listaDaMesa.atualizarTopo;

  /* O motor do copiloto: quem está pensando, no painel. Não é lista, e não
     precisa vir junto de cada página. */
  useEffect(() => {
    if (!idDoUsuario) return;
    api
      .motor()
      .then((motor) => mudar({ motor }))
      .catch(() => {});
  }, [idDoUsuario, mudar]);

  /*
   * O que cada vista precisa buscar — e o sinal de que está buscando.
   *
   * `calls` e `files` saíram: buscavam /api/gravacoes e /api/documentos para
   * `estado.gravacoes` e `estado.docs`, que nenhum componente lia (Reuniões tem
   * a própria lista, Documentos usa o acervo). Eram dois pedidos competindo com
   * a primeira pintura da vista — e a leitura de pasta não é barata.
   *
   * A saída antecipada é o que mantém `carregandoVista` honesto: vista que não
   * busca nada não pode acender o sinal de "lendo…".
   */
  const carregarVista = useCallback(
    async (vista) => {
      if (!["fluxos", "settings", "avisos", "tarefas", "agenda", "rapidas", "agentes_ia"].includes(vista)) return;
      mudar({ carregandoVista: vista, erroDaVista: null });
      try {
        if (vista === "agentes_ia") {
          /*
           * A Central da IA é UMA tela com cinco seções, e cada seção vive de uma
           * área diferente do servidor: agentes (`agentes:ver`), fluxos
           * (`copiloto:ver`) e preferências (`config:ver`). As três vêm juntas
           * porque a pessoa troca de seção com um toque — buscar na troca faria
           * cada aba piscar "lendo…" —, e as duas de fora entram com `catch`:
           * quem só alcança os agentes continua com a tela de agentes de pé, e a
           * seção que faltou diz quem libera.
           */
          const [r, deFluxos, preferencias, motor] = await Promise.all([
            api.agentesIa(),
            api.fluxos().catch(() => null),
            api.ajustes().catch(() => null),
            api.motor().catch(() => null),
          ]);
          mudar({
            agentesIa: r.agentes ?? [],
            gatilhosIa: r.gatilhos ?? [],
            tiposIa: r.tipos ?? [],
            ferramentasIa: r.ferramentas ?? [],
            etapas: r.etapas ?? [],
            delegadas: r.delegadas ?? [],
            /* O que a IA SABE: playbook (nas etapas), documentos por tese e o
               texto de cada cobrança. */
            tesesDaIa: r.teses ?? [],
            cobrancaDaIa: r.cobranca ?? null,
            freiosDaIa: r.freios ?? [],
            ...(deFluxos
              ? {
                  fluxos: deFluxos.fluxos,
                  autonomia: deFluxos.autonomia,
                  gatilhos: deFluxos.gatilhos ?? [],
                  catalogos: deFluxos.catalogos ?? {},
                  envio: deFluxos.envio,
                }
              : {}),
            ...(preferencias ? { ajustes: preferencias.ajustes, ambiente: preferencias.ambiente } : {}),
            ...(motor ? { motor } : {}),
          });
        } else if (vista === "fluxos") {
          const r = await api.fluxos();
          /* `delegadas` é a regra EM AÇÃO: os atendimentos que a IA conduz agora.
             É a única ligação honesta entre esta tela e a mesa — `fluxos` não
             guarda vínculo com caso nenhum. */
          mudar({
            fluxos: r.fluxos,
            autonomia: r.autonomia,
            gatilhos: r.gatilhos ?? [],
            catalogos: r.catalogos ?? {},
            envio: r.envio,
            delegadas: r.delegadas ?? [],
          });
        } else if (vista === "avisos") {
          mudar({ avisosTodos: (await api.avisos(api.TETO_DE_AVISOS)).avisos ?? [] });
        } else if (vista === "tarefas") {
          /* O recorte vem da URL, como na agenda: busca e caso são pedido ao
             SERVIDOR (`/api/tarefas` filtra por `q` e por `caso`), e não peneira
             na tela — o quadro é o escritório inteiro, e contar o que a página
             trouxe foi o erro que a lista da mesa já cometeu. `navRef` porque
             esta função é estável. */
          const { termo, casoDoQuadro } = navRef.current;
          const r = await api.tarefas({ q: termo || undefined, caso: casoDoQuadro || undefined });
          mudar({
            tarefas: r.tarefas ?? [],
            contadoresTarefas: r.contadores ?? {},
            cargaTarefas: r.carga ?? [],
            prazosDaSemana: r.prazos ?? [],
          });
        } else if (vista === "agenda") {
          /* O intervalo mora na URL (a agenda navega por semana): ler daqui é o
             que faz "próxima semana" buscar a próxima semana em vez de recarregar
             a mesma. `navRef` e não `nav`: esta função é estável e não pode
             renascer a cada troca de query. */
          const { de, ate } = navRef.current;
          const r = await api.agenda(de, ate);
          mudar({ agendaDias: r.dias ?? [], contadoresAgenda: r.contadores ?? {}, fusoDaAgenda: r.fuso ?? null });
        } else if (vista === "rapidas") {
          const r = await api.rapidas();
          mudar({ rapidas: r.rapidas ?? [], categoriasRapidas: r.categorias ?? [], variaveisRapidas: r.variaveis ?? [] });
        } else if (vista === "settings") {
          const [ajustes, motor, conexaoWa] = await Promise.all([
            api.ajustes(),
            api.motor(),
            /* A conexão não pode derrubar a tela de Ajustes: provedor fora do ar
               é informação, não erro de página. */
            api.conexaoWhatsapp().catch(() => null),
          ]);
          mudar({ ajustes: ajustes.ajustes, ambiente: ajustes.ambiente, motor, conexaoWa });
        }
      } catch (e) {
        avisar(e.message);
        /* A vista precisa distinguir "não veio" de "não tem": sem isto a Caixa
           lia para sempre e Fluxos afirmava vazio depois de uma recusa. */
        mudar({ erroDaVista: { vista, status: e.status ?? 0, mensagem: e.message, daCasa: Boolean(e.daCasa) } });
      } finally {
        /* Só apaga o sinal se ainda é o desta vista: trocar de tela no meio do
           pedido deixaria a vista nova sem "lendo…" e a antiga acesa. */
        mudar((a) => (a.carregandoVista === vista ? { carregandoVista: null } : {}));
      }
    },
    [avisar, mudar],
  );

  const abrirSessao = useCallback(async () => {
    try {
      const r = await api.eu();
      const provisoria = Boolean(r.usuario?.precisa_trocar_senha);
      rascunhos.usarSetor(r.workspace?.chave, r.usuario?.id);
      if (r.usuario && !provisoria) push.automatico();
      mudar({
        usuario: provisoria ? null : r.usuario,
        trocaDeSenha: provisoria ? r.usuario : null,
        workspace: r.workspace ?? null,
        workspaces: r.workspaces ?? [],
        pronto: true,
        erroLogin: null,
      });
      /* A lista não é buscada aqui: `usePaginacao` recomeça sozinho quando o
         usuário aparece, porque `buscarMesa` depende dele. */
    } catch (e) {
      /* 401 aqui é o esperado: ninguém entrou ainda. Qualquer outra coisa —
         servidor mudo, prazo estourado, 500 — é motivo, e o lugar do motivo é a
         própria porta: o balão de seis segundos passava antes de a pessoa ler,
         e sobrava um formulário que recusa tudo sem dizer por quê. */
      mudar({ usuario: null, pronto: true, ...(e.status !== 401 ? { erroLogin: e.message } : {}) });
      if (e.status !== 401) setRecado(e.message);
    }
  }, [mudar]);

  useEffect(() => {
    abrirSessao();
  }, [abrirSessao]);

  /*
   * Sessão que cai no meio da navegação volta para a PORTA, com o motivo.
   *
   * 401 depois de a pessoa já estar dentro não é erro de uma tela: é o fim da
   * sessão, e toda tela dali para a frente falharia igual — a mesa virava um
   * carrossel de balões de seis segundos sobre conteúdo que não ia mais chegar.
   * O recado é escrito em `erroLogin`, que é onde a tela de entrar já fala.
   */
  useEffect(
    () =>
      api.seASessaoCair(() =>
        mudar((a) =>
          a.usuario
            ? { usuario: null, caso: null, casoErro: null, erroLogin: "Sua sessão terminou. Entre de novo para continuar." }
            : {},
        ),
      ),
    [mudar],
  );

  /* --------------------------------------------- a rota manda no que carrega */

  /* Arquivo e áudio presos só existem nesta aba: fechar ou recarregar com
     algum deles pergunta antes, como o navegador faz com formulário. */
  useEffect(() => {
    const segurar = (ev) => {
      const a = ref.current;
      if (!a.anexosPendentes?.length && !a.recado_voz && !rascunhos.temAlgoPreso()) return;
      ev.preventDefault();
      ev.returnValue = "";
    };
    addEventListener("beforeunload", segurar);
    return () => removeEventListener("beforeunload", segurar);
  }, []);

  const casoDaRedacao = useRef(null);
  useEffect(() => {
    if (!estado.usuario || !nav.casoId) return;
    /* Quem marca lida é o `carregarCaso`, com a conversa já lida do servidor. */
    carregarCaso(nav.casoId);
    /*
     * O que estava sendo escrito no caso de antes vai para a GAVETA dele
     * (`estado/rascunhos.js`), e o deste sai da gaveta. O texto já está lá — a
     * redação guarda a cada tecla. Recado gravando é pausado e vira anexo
     * daquele caso: continuar gravando mandaria a voz para o cliente errado.
     */
    const antes = casoDaRedacao.current;
    casoDaRedacao.current = nav.casoId;
    if (antes === nav.casoId) return;
    if (antes) {
      const a = ref.current;
      if (recadoRef.current) acoesRef.current?.pararRecado?.({ enviar: false });
      rascunhos.guardarResto(antes, {
        anexosPendentes: a.anexosPendentes,
        recado_voz: a.recado_voz?.bloco && !a.recado_voz.enviando ? a.recado_voz : null,
        respondendo: a.respondendo,
        /* `editando` não vai: o campo da edição tem o texto da mensagem velha,
           e voltar com ela marcada e o rascunho no campo faria o Enter
           reescrever a mensagem com o rascunho. */
        modoComposer: a.modoComposer,
      });
    }
    const guardado = rascunhos.tirarResto(nav.casoId);
    /*
     * Trocar de caso é trocar de DESTINATÁRIO, não receber evento.
     *
     * A regra da casa protege o rascunho de evento do servidor; aqui é outra
     * pessoa do outro lado. A citação e a edição pendurada apontavam para
     * mensagem de OUTRA conversa — e o texto já digitado seguia no campo,
     * porque `/atendimentos/:id` casa o mesmo elemento e a redação não remonta.
     */
    mudar({
      sugestao: null,
      sugestaoOculta: false,
      /* O ciclo é DAQUELE caso: mostrar o passo do anterior seria a IA
         trabalhando na conversa errada. */
      iaEmAcao: null,
      rascunhoPeca: null,
      anexosPendentes: [],
      recado_voz: null,
      respondendo: null,
      editando: null,
      modoComposer: "cliente",
      ...guardado,
      sementeRascunho: { texto: rascunhos.lerTexto(nav.casoId), n: Date.now(), caso: nav.casoId, troca: true },
    });
  }, [carregarCaso, estado.usuario, mudar, nav.casoId]);

  /*
   * A vista busca quando se chega nela — e quando o RECORTE dela muda.
   *
   * `nav.de`/`nav.ate` entram nas dependências por causa da agenda: trocar de
   * dia, de semana ou de mês reescreve o intervalo na URL, e sem observar isso a
   * tela redesenhava a grade do mês com os dados da semana — grade certa,
   * conteúdo errado, e nada na tela dizendo que faltava. Nas outras vistas os
   * dois são string vazia e nunca mudam, então nenhuma busca extra acontece.
   */
  useEffect(() => {
    if (!estado.usuario) return;
    /* Tarefas tem efeito PRÓPRIO, logo abaixo: o recorte dela é pedido ao
       servidor e precisa de espera na digitação. Dentro deste efeito, o termo
       nas dependências faria a Caixa e Fluxos — que peneiram na tela — buscarem
       de novo a cada tecla. */
    if (nav.vista === "tarefas") return;
    carregarVista(nav.vista);
  }, [carregarVista, estado.usuario, nav.vista, nav.de, nav.ate]);

  /*
   * O quadro de Tarefas: a busca e o caso são recorte DO SERVIDOR.
   *
   * `/api/tarefas` já filtrava por `q` e por `caso`, e a tela não tinha endereço
   * para nenhum dos dois — a busca não existia, e "as tarefas deste caso" (a
   * porta que a ficha do atendimento abre) não tinha para onde apontar. Peneirar
   * na tela não servia: o quadro é o escritório inteiro, e contar a página
   * carregada foi o erro que a lista da mesa já cometeu.
   */
  useEffect(() => {
    if (!estado.usuario || nav.vista !== "tarefas") return undefined;
    const t = setTimeout(() => carregarVista("tarefas"), nav.termo ? 250 : 0);
    return () => clearTimeout(t);
  }, [carregarVista, estado.usuario, nav.casoDoQuadro, nav.termo, nav.vista]);

  /* ------------------------------------------------ eventos e tamanho */

  useCanalDeEventos({
    usuario: estado.usuario,
    carregarMesa,
    carregarVista,
    lerCopiloto,
    listaDaMesa,
    mudar,
    navRef,
    releitura,
    relogioPresenca,
  });

  /* ---------------------------------------------------- recado de voz */

  /* O recado de voz para o cliente. Gravar REUNIÃO não mora mais aqui: o motor é
     `funcionalidades/reunioes/useGravacaoReuniao.ts`, dentro da aba Reunião — o
     único lugar que mostra que está gravando. */
  const recadoRef = useRef(null);

  /* ------------------------------------------------------------- ações */

  /**
   * Diz quando a mensagem ficou só no CRM.
   *
   * Sem credencial, com o WhatsApp desconectado ou com a sessão em outro
   * processo, o provedor responde `via: "ensaio"`: a mensagem entra na conversa e
   * NÃO sai. Silenciar isso é o pior dos mundos — quem mandou fica esperando
   * resposta de quem nunca recebeu nada.
   */
  const avisarSeFoiEnsaio = useCallback(
    (r) => {
      /* Com mais de um número o servidor diz QUAL está fora (`motivo`): "o
         WhatsApp está desconectado" com o principal verde no alto da mesa é
         uma frase que a pessoa não consegue conferir. */
      if (r?.via === "ensaio")
        avisar(
          r.motivo && r.motivo !== "o WhatsApp está desconectado"
            ? `Gravado no CRM, mas NÃO enviado: ${r.motivo}.`
            : "Gravado no CRM, mas NÃO enviado: o WhatsApp está desconectado. Reconecte em Ajustes → Conexão do WhatsApp.",
        );
    },
    [avisar],
  );

  const acoes = useMemo(() => {
    const casoAberto = () => navRef.current.casoId;
    const recarregarCaso = () => carregarCaso(casoAberto(), { comCopiloto: false });
    /* O que cada área de `estado/` recebe. Todos estáveis: refs, `useCallback`
       sem dependência que mude, e as funções acima. */
    const c = {
      abortoMinuta,
      acoesRef,
      avisar,
      avisarSeFoiEnsaio,
      carregarCaso,
      carregarMesa,
      carregarVista,
      casoAberto,
      lerCopiloto,
      listaDaMesa,
      mudar,
      navRef,
      ocupadoRef,
      recadoRef,
      recarregarCaso,
      ref,
      tentar,
    };

    return {
      mudar,
      avisar,
      tentar,
      carregarMesa,

      /*
       * Etiqueta: grava e relê o caso aberto.
       *
       * Fica no estado, e não na peça de tela, porque quem mexe é o cabeçalho da
       * conversa E a linha da mesa — e as duas precisam do MESMO caminho de
       * recarga, senão uma delas mostra o rótulo velho até alguém trocar de
       * tela. A releitura da lista é de quem chama (nem toda etiquetagem parte
       * da mesa).
       */
      etiquetar: async (fn) => {
        const r = await fn();
        await recarregarCaso();
        return r;
      },
      carregarCaso,
      carregarVista,
      /* A rolagem da mesa pedindo a próxima página, e o "tentar de novo" do
         rodapé quando a rede falhou no meio. */
      maisDaMesa: listaDaMesa.mais,
      tentarMaisDaMesa: listaDaMesa.tentarDeNovo,

      entrar: async (email, senha) => {
        try {
          await api.entrar(email, senha);
          await abrirSessao();
        } catch (e) {
          mudar({ erroLogin: e.message });
        }
      },
      /* A troca da senha provisória relança: quem mostra a recusa é a própria
         tela de troca, ao lado do campo — não um balão de seis segundos. */
      trocarSenhaProvisoria: async (atual, nova) => {
        await api.trocarSenha(atual, nova);
        await abrirSessao();
        mudar({ recemChegado: true });
      },
      esquecerChegada: () => mudar({ recemChegado: false }),
      /* O botão de sair é o retrato de 34px no pé do trilho, ao lado do sino:
         clique perdido derrubava a sessão e recarregava a página, levando o
         rascunho que estava no campo. */
      sair: async () => {
        const ok = await confirmar({
          titulo: "Sair do sistema?",
          texto: "A mesa fecha e é preciso entrar de novo com e-mail e senha. Mensagem escrita e não enviada se perde.",
          acao: "Sair",
          perigo: true,
        });
        if (!ok) return;
        /* O aparelho deixa de avisar quem saiu; quem entrar depois reinscreve. */
        await push.desligar(false).catch(() => {});
        await api.sair().catch(() => {});
        location.assign("/");
      },

      /**
       * Trocar de escritório é recarregar a mesa inteira.
       *
       * O cookie decide de qual banco vem TUDO — lista, caso aberto, documento,
       * equipe, ajuste. Recarregar é a única forma de garantir que nada do
       * escritório anterior sobrou na tela; costurar recarga pedaço por pedaço
       * deixaria a conversa de um lado com a lista do outro.
       */
      trocarEscritorio: async (chave) => {
        if (!chave || chave === ref.current.workspace?.chave) return;
        await tentar("escritorio", async () => {
          await api.trocarWorkspace(chave);
          location.assign("/atendimentos");
        });
      },

      fecharRecado: () => setRecado(null),

      ...acoesDaConversa(c),
      ...acoesDoCaso(c),
      ...acoesDeTarefas(c),
      ...acoesDoContato(c),
      ...acoesDeVoz(c),
      ...acoesDoWhatsapp(c),
      ...acoesDaMensagem(c),
    };
  }, [abrirSessao, avisar, carregarCaso, carregarMesa, carregarVista, lerCopiloto, mudar, tentar]);

  acoesRef.current = acoes;

  /*
   * O que a lista paginada sabe entra no estado só aqui.
   *
   * `mesa` continua sendo o nome que a mesa inteira já lê — a diferença é que
   * agora é a PÁGINA carregada, e vem com `totalDaMesa` do lado para a tela
   * poder dizer "200 de 528" em vez de fingir que 200 é tudo.
   */
  const visto = useMemo(
    () => ({
      ...estado,
      mesa: listaDaMesa.itens,
      totalDaMesa: listaDaMesa.total,
      mesaCarregando: listaDaMesa.carregando,
      mesaErro: listaDaMesa.erro,
      mesaTemMais: listaDaMesa.temMais,
    }),
    [estado, listaDaMesa.itens, listaDaMesa.total, listaDaMesa.carregando, listaDaMesa.erro, listaDaMesa.temMais],
  );

  return (
    <EstadoContexto.Provider value={visto}>
      <AcoesContexto.Provider value={acoes}>
        <OcupadoContexto.Provider value={ocupado}>
          <RecadoContexto.Provider value={recado}>{children}</RecadoContexto.Provider>
        </OcupadoContexto.Provider>
      </AcoesContexto.Provider>
    </EstadoContexto.Provider>
  );
}

/* ------------------------------------------------------------ derivados */

/* Pela janela, e não por `useEstado()`: o booleano só muda ao cruzar o limiar,
   então arrastar a borda não redesenha quem só quer saber se é celular. */
export const useCelular = () => useJanela((j) => j.largura < 760);
export const useTablet = () => useJanela((j) => j.largura >= 760 && j.largura < 1180);

/**
 * O caso aberto agora, cruzando a rota com a lista carregada.
 *
 * `mesa` é uma PÁGINA (200 de 528), então a linha do caso aberto pode não estar
 * nela: link de caso antigo recebido por mensagem, busca digitada com um caso
 * aberto, pastilha que exclui o caso aberto. Quando falta, vale o que
 * `/api/mesa/:id` respondeu — o dado existe, era só a lista que não o tinha.
 */
export function useCasoAtual() {
  const { mesa, caso, etapas } = useEstado();
  const { casoId } = useNavegacao();
  return useMemo(() => mesa.find((c) => c.id === casoId) ?? linhaDoCaso(caso, etapas, casoId), [mesa, caso, etapas, casoId]);
}

/**
 * Marcar um anexo como prova, de dentro do visor.
 *
 * Fica aqui porque as DUAS portas do visor precisam da mesma peça: abrindo pela
 * bolha (`Trilha.jsx`) o botão existia, abrindo pela aba Anexos ele não vinha — o
 * mesmo visor mudava de comportamento conforme por onde se entrou.
 */
export function useAcaoDeProva() {
  const { caso, provas } = useEstado();
  const { marcarProva } = useAcoes();
  /* Sem `?? []` aqui: o array literal nasce novo a cada render e o `useMemo`
     nunca memoizaria — o visor receberia um `acao` diferente sempre. */
  const mensagens = caso?.mensagens;
  return useMemo(
    () => ({
      rotulo: "MARCAR COMO PROVA",
      titulo: "Marcar este anexo como prova do caso",
      marcado: (a) => (provas ?? []).some((p) => p.mensagem_id === a.id),
      aoAgir: (a) => {
        const m = (mensagens ?? []).find((x) => x.id === a.id);
        if (m) marcarProva(m);
      },
    }),
    [provas, mensagens, marcarProva],
  );
}

/** Permissão da pessoa logada — a tela esconde o que ela não alcança. */
export function usePode() {
  const { usuario } = useEstado();
  return useCallback(
    (permissao) => {
      if (!usuario) return false;
      /* Sem atalho por papel: a lista do master já vem inteira do servidor, menos
         o que é só do dev (a tela Sistema) — um `papel === "master"` aqui
         mostraria a ele a porta que o servidor recusa. */
      if (usuario.permissoes?.includes("*")) return true;
      return Boolean(usuario.permissoes?.includes(permissao));
    },
    [usuario],
  );
}

/** Como a tela conta o escopo da delegação em uma linha. */
export function useEscopoEmPalavras() {
  const { etapas } = useEstado();
  return useCallback(
    (d) => {
      const rotulo = etapas.find((e) => e.chave === d.etapa)?.rotulo ?? d.etapa ?? "";
      if (d.modo === "total") return "Autonomia total, respeitados os limites";
      if (d.modo === "ate") return `Conduz até "${rotulo}", depois devolve`;
      if (d.modo === "a_partir") return `Assume a partir de "${rotulo}"`;
      return "Cobertura temporária, até você voltar a digitar";
    },
    [etapas],
  );
}
